import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { Upload, Eye, Palette, Building, Crown, X, AlertTriangle, Users, Lock, Type, Image } from 'lucide-react';

interface UserBranding {
  id?: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  display_type: 'logo_only' | 'name_only' | 'logo_and_name';
  is_shared_with_team?: boolean;
  created_by?: string;
  creator?: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface BrandingAccess {
  hasAccess: boolean;
  plan: string;
  message: string;
}

export function BrandingSettings() {
  const { session } = useAuth();
  const [branding, setBranding] = useState<UserBranding>({
    company_name: '',
    logo_url: null,
    primary_color: '#3B82F6',
    secondary_color: '#1F2937',
    text_color: '#FFFFFF',
    display_type: 'logo_and_name',
    is_shared_with_team: false
  });
  const [access, setAccess] = useState<BrandingAccess | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  useEffect(() => {
    loadBrandingData();
    checkAccess();
  }, [session]);

  const checkAccess = async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/branding/check-access`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const accessData = await response.json();
        setAccess(accessData);
      }
    } catch (error) {
      console.error('Error checking branding access:', error);
    }
  };

  const loadBrandingData = async () => {
    if (!session?.access_token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/branding`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const brandingData = await response.json();
        setBranding(brandingData);
      } else if (response.status === 403) {
        // User doesn't have access to white label
        console.log('User does not have white label access');
      }
    } catch (error) {
      console.error('Error loading branding:', error);
      setError('Fehler beim Laden der Branding-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!session?.access_token) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('company_name', branding.company_name);
      formData.append('primary_color', branding.primary_color);
      formData.append('secondary_color', branding.secondary_color);
      formData.append('text_color', branding.text_color);
      formData.append('display_type', branding.display_type);
      formData.append('is_shared_with_team', String(branding.is_shared_with_team || false));
      
      if (logo) {
        formData.append('logo', logo);
      }

      const response = await fetch(`${API_BASE}/api/branding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (response.ok) {
        const updatedBranding = await response.json();
        setBranding(updatedBranding);
        setLogo(null);
        setSuccess('Branding-Einstellungen erfolgreich gespeichert!');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fehler beim Speichern der Branding-Einstellungen');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      setError('Fehler beim Speichern der Branding-Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/branding/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setBranding(prev => ({ ...prev, logo_url: null }));
        setLogo(null);
        setSuccess('Logo erfolgreich entfernt!');
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      setError('Fehler beim Entfernen des Logos');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show upgrade prompt if user doesn't have access
  if (access && !access.hasAccess) {
    return (
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Crown className="w-6 h-6 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 mb-2">White Label Branding</h3>
              <p className="text-amber-700 mb-4">
                {access.message}
              </p>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <h4 className="font-medium text-gray-900 mb-2">Was ist White Label Branding?</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Dein Logo anstelle von "BeyondPages"</li>
                  <li>• Dein Firmenname in der Fußzeile</li>
                  <li>• Professioneller Auftritt gegenüber Kunden</li>
                  <li>• Keine "Powered by BeyondPages" Hinweise</li>
                </ul>
              </div>
              <div className="mt-4">
                <a
                  href="/qr/pricing"
                  className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Jetzt upgraden
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header mit Erklärung */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Palette className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">White Label Branding</h3>
            <p className="text-sm text-blue-700 mt-1">
              Personalisiere deine QR-Code-Seiten mit deinem Logo und Firmennamen. 
              Deine Besucher sehen dann <strong>dein Branding</strong> anstelle von "BeyondPages".
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Logo Upload */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          Firmen-Logo
        </h4>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            {(logo || branding.logo_url) ? (
              <div className="space-y-2">
                <img 
                  src={logo ? URL.createObjectURL(logo) : branding.logo_url || ''} 
                  alt="Logo Preview" 
                  className="mx-auto h-16 object-contain"
                />
                <p className="text-sm text-gray-600">
                  {logo ? logo.name : 'Aktuelles Logo'}
                </p>
                <div className="flex justify-center space-x-2">
                  <button
                    onClick={() => setLogo(null)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Neues Logo wählen
                  </button>
                  {branding.logo_url && (
                    <button
                      onClick={handleRemoveLogo}
                      className="text-sm text-red-500 hover:text-red-700 flex items-center"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Logo entfernen
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="text-gray-600">Logo hochladen (PNG, JPG, SVG, WebP)</p>
                <p className="text-xs text-gray-500">Empfohlen: 200x60px, max. 2MB</p>
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      {/* Firmenname */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Building className="w-5 h-5 mr-2" />
          Firmenname
        </h4>
        
        <input
          type="text"
          value={branding.company_name}
          onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
          placeholder="z.B. Deine Firma GmbH"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        
        <p className="text-sm text-gray-500 mt-2">
          Wird in der Fußzeile deiner QR-Code-Seiten angezeigt
        </p>
      </div>

      {/* Anzeigeoptionen */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Eye className="w-5 h-5 mr-2" />
          Anzeigeoptionen
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Was soll angezeigt werden?
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="display_type"
                  value="logo_and_name"
                  checked={branding.display_type === 'logo_and_name'}
                  onChange={(e) => setBranding(prev => ({ ...prev, display_type: 'logo_and_name' }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 flex items-center">
                  <Image className="w-4 h-4 mr-1" />
                  <Type className="w-4 h-4 mr-1" />
                  Logo und Firmenname
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="display_type"
                  value="logo_only"
                  checked={branding.display_type === 'logo_only'}
                  onChange={(e) => setBranding(prev => ({ ...prev, display_type: 'logo_only' }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 flex items-center">
                  <Image className="w-4 h-4 mr-1" />
                  Nur Logo
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="display_type"
                  value="name_only"
                  checked={branding.display_type === 'name_only'}
                  onChange={(e) => setBranding(prev => ({ ...prev, display_type: 'name_only' }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 flex items-center">
                  <Type className="w-4 h-4 mr-1" />
                  Nur Firmenname
                </span>
              </label>
            </div>
          </div>
          
          {branding.display_type === 'logo_only' && !branding.logo_url && !logo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Bitte laden Sie ein Logo hoch, wenn Sie "Nur Logo" auswählen.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Farben */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Palette className="w-5 h-5 mr-2" />
          Farben
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primärfarbe (Hintergrund)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={branding.primary_color}
                onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={branding.primary_color}
                onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sekundärfarbe (Hintergrund)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={branding.secondary_color}
                onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={branding.secondary_color}
                onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Textfarbe (Firmenname)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={branding.text_color}
                onChange={(e) => setBranding(prev => ({ ...prev, text_color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={branding.text_color}
                onChange={(e) => setBranding(prev => ({ ...prev, text_color: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Team Sharing */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Team-Freigabe
        </h4>
        
        <div className="space-y-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={branding.is_shared_with_team || false}
              onChange={(e) => setBranding(prev => ({ ...prev, is_shared_with_team: e.target.checked }))}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Mit Team teilen</span>
              <p className="text-sm text-gray-500 mt-1">
                Wenn aktiviert, können alle Team-Mitglieder dieses Branding für ihre QR-Codes verwenden.
                Nur du kannst es bearbeiten oder löschen.
              </p>
            </div>
          </label>
          
          {branding.is_shared_with_team && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-blue-700">
                  Team-Mitglieder können dieses Branding verwenden, aber nicht ändern.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Eye className="w-5 h-5 mr-2" />
          Vorschau
        </h4>
        
        <div 
          className="border border-gray-200 rounded-lg p-4"
          style={{ 
            background: `linear-gradient(to bottom right, ${branding.primary_color}, ${branding.secondary_color})`
          }}
        >
          <div className="text-center space-y-2">
            {/* Header Preview */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center justify-center gap-3">
                {branding.display_type !== 'name_only' && (logo || branding.logo_url) && (
                  <img 
                    src={logo ? URL.createObjectURL(logo) : branding.logo_url || ''} 
                    alt="Logo" 
                    className="h-10 object-contain"
                  />
                )}
                {branding.display_type !== 'logo_only' && branding.company_name && (
                  <span 
                    className="font-bold text-xl"
                    style={{ color: branding.text_color }}
                  >
                    {branding.company_name}
                  </span>
                )}
              </div>
            </div>
            
            {/* Content Preview */}
            <div className="bg-white/90 rounded-lg p-6 mt-4">
              <h3 className="font-bold text-lg text-gray-900">
                Spotify Playlist
              </h3>
              <p className="text-gray-600">Meine Lieblings-Musik</p>
              <button className="mt-4 bg-green-600 text-white px-6 py-2 rounded-full">
                In Spotify öffnen
              </button>
            </div>
            
            {/* Footer Preview */}
            <div className="text-xs mt-4" style={{ color: branding.text_color }}>
              {branding.display_type !== 'logo_only' && branding.company_name ? (
                <p>© 2024 {branding.company_name}</p>
              ) : branding.display_type === 'logo_only' ? (
                <p className="opacity-50">Logo wird angezeigt</p>
              ) : (
                <p className="opacity-50">Kein Branding</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Speichern...
            </>
          ) : (
            'Branding speichern'
          )}
        </button>
      </div>
    </div>
  );
} 